import React, { useEffect, useState } from 'react'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'

const AuthCallback = ({ onBackToLogin }) => {
  const [status, setStatus] = useState('processing') // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Processing email confirmation...')
  const [isNavigating, setIsNavigating] = useState(false)
  const { signIn } = useAuth()

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Extract auth data from URL hash (Supabase format)
        const urlHash = window.location.hash
        const urlParams = new URLSearchParams(window.location.search)
        
        console.log('🔗 AuthCallback: Processing email confirmation')
        console.log('🔗 AuthCallback: URL hash:', urlHash)
        console.log('🔗 AuthCallback: URL params:', urlParams.toString())

        // Check for auth tokens in hash (Supabase email confirmation format)
        let authParams = new URLSearchParams()
        if (urlHash) {
          // Remove the # and parse
          authParams = new URLSearchParams(urlHash.substring(1))
        }

        // Also check query params as fallback
        for (const [key, value] of urlParams.entries()) {
          authParams.set(key, value)
        }

        const accessToken = authParams.get('access_token')
        const refreshToken = authParams.get('refresh_token')
        const errorParam = authParams.get('error')
        const errorDescription = authParams.get('error_description')
        const type = authParams.get('type')

        console.log('🔗 AuthCallback: Extracted params:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          type,
          error: errorParam,
          errorDescription
        })

        // Check for errors first
        if (errorParam) {
          console.error('🔗 AuthCallback: Email confirmation error:', errorParam, errorDescription)
          setStatus('error')
          setMessage(errorDescription || `Confirmation failed: ${errorParam}`)
          return
        }

        // Check if this is an email confirmation
        if (type === 'signup' || accessToken) {
          console.log('🔗 AuthCallback: Email confirmation detected')
          setStatus('success')
          setMessage('Email confirmed successfully! You can now sign in to access E8 Tracker.')
          
          // Clear the URL hash to clean up the browser
          if (window.history.replaceState) {
            window.history.replaceState(null, null, window.location.pathname)
          }
        } else {
          console.log('🔗 AuthCallback: No auth tokens found, treating as general callback')
          setStatus('success')
          setMessage('Account verification completed successfully! You can now sign in.')
        }

      } catch (error) {
        console.error('🔗 AuthCallback: Error processing confirmation:', error)
        setStatus('error')
        setMessage('An error occurred while processing your email confirmation. Please try signing in.')
      }
    }

    handleEmailConfirmation()
  }, [])

  const handleBackToLogin = () => {
    console.log('🔗 AuthCallback: Navigating back to login...')
    setIsNavigating(true)

    // Always normalize URL first (remove hash/params)
    try {
      if (window.history.replaceState) {
        window.history.replaceState(null, null, '/')
      }
    } catch {}

    // Primary: If callback provided, use it (App will render login)
    if (onBackToLogin) {
      onBackToLogin()
    }

    // Robust fallbacks to ensure we actually land on root login
    // 1) Try pushState to root to trigger React rerender
    try {
      window.history.pushState({}, '', '/')
    } catch {}

    // 2) Ensure hash cleared to avoid callback detection
    try {
      window.location.hash = ''
    } catch {}

    // 3) Hard navigate immediately to guarantee exit from callback page
    try {
      window.location.replace('/')
      return
    } catch {}

    // 4) Safety net: if replace failed for any reason, try again after a short delay
    setTimeout(() => {
      setIsNavigating(false)
      if (window.location.pathname !== '/') {
        window.location.href = '/'
      }
    }, 250)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {status === 'processing' && (
            <div className="animate-spin mx-auto h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          )}
          {status === 'success' && (
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
          )}
          {status === 'error' && (
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          )}
          
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {status === 'processing' && 'Confirming Email...'}
            {status === 'success' && 'Email Confirmed!'}
            {status === 'error' && 'Confirmation Failed'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            E8 Tracker Account Verification
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center space-y-6">
            
            {/* Status Message */}
            <div className={`p-4 rounded-md ${
              status === 'success' ? 'bg-green-50 border border-green-200' :
              status === 'error' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-sm ${
                status === 'success' ? 'text-green-700' :
                status === 'error' ? 'text-red-700' :
                'text-blue-700'
              }`}>
                {message}
              </p>
            </div>

            {/* Success Actions */}
            {status === 'success' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-blue-800">
                      What's Next?
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Your email has been verified successfully</li>
                        <li>You now have full access to E8 Tracker</li>
                        <li>Click "Sign In" below to get started</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Actions */}
            {status === 'error' && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <div className="text-left">
                  <h3 className="text-sm font-medium text-amber-800">
                    Troubleshooting
                  </h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Check if the email link has expired</li>
                      <li>Try signing in with your credentials</li>
                      <li>Contact support if issues persist</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleBackToLogin}
                disabled={isNavigating}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isNavigating ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Redirecting...
                  </div>
                ) : (
                  status === 'success' ? 'Sign In Now' : 'Back to Sign In'
                )}
              </button>
              
              {status === 'error' && (
                <button
                  onClick={() => window.location.reload()}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              )}
            </div>

            {/* Support Info */}
            <div className="text-xs text-gray-500">
              <p>
                Need help? Contact support: 
                <br />
                <span className="font-medium">support@e8tracker.au</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthCallback